import { useEffect, useRef } from 'react';

/**
 * MED-1239 — bounded background poll for async post-session AI insights.
 *
 * Post-session AI insights (Session Themes, Airtime Balance) are computed
 * asynchronously server-side. The read side returns `status: "pending"` and
 * self-heals on the *next* read (see medley-api `SessionThemesService.getForView`,
 * whose own comment is "the next load serves the row"). The three post-session
 * views historically fetched their bundle once on mount and never issued that
 * later load, so a card that mounted while its insight was still `pending` was
 * stuck on its loader until a manual hard refresh.
 *
 * This hook is the shared "later load" mechanism. While `pending` is true (any
 * tracked envelope reads `pending`) and polling is `enabled`, it re-invokes the
 * caller's `poll()` on a fixed interval. It stops the moment `pending` flips to
 * false — i.e. every tracked envelope has reached a terminal state
 * (`available` / `not_available`) — satisfying AC3 (no re-poll, no flip-flop).
 *
 * `poll()` is the caller's responsibility to keep AC5-safe: it must refetch and
 * merge ONLY the AI envelope fields into view state, never reseeding editable
 * working copies (attendance, feedback, notes, commitments).
 *
 * Bounded (AC4): the interval also self-terminates at `maxWindowMs` from when
 * polling began, mirroring the backend's `PENDING_WINDOW_MS` (~30 min after
 * scheduled end). In practice the backend stops reporting `pending` once its
 * window closes, so AC3 ends the poll first; this ceiling is a backstop against
 * a pathological "pending forever" backend so the client never polls
 * indefinitely. A card still `pending` at the ceiling settles on the next
 * natural load, exactly as the ticket describes.
 */

/** Poll cadence while an insight is still generating. */
const DEFAULT_INTERVAL_MS = 15_000;

/**
 * Hard ceiling on how long a single mount will keep polling. Mirrors medley-api
 * `SessionThemesService.PENDING_WINDOW_MS` (30 min); see the AC4 note above.
 */
const DEFAULT_MAX_WINDOW_MS = 30 * 60 * 1000;

export interface UseAiInsightPollingParams {
  /** Master switch — false in DEV preview / when there is no session to poll. */
  enabled: boolean;
  /** True while any tracked AI envelope reads `pending`. */
  pending: boolean;
  /** Refetch + merge ONLY the AI envelope fields into view state (AC5-safe). */
  poll: () => void | Promise<void>;
  /** Poll cadence (ms). Defaults to 15s. */
  intervalMs?: number;
  /** Bounded polling ceiling (ms). Defaults to 30 min (backend pending window). */
  maxWindowMs?: number;
}

export function useAiInsightPolling({
  enabled,
  pending,
  poll,
  intervalMs = DEFAULT_INTERVAL_MS,
  maxWindowMs = DEFAULT_MAX_WINDOW_MS,
}: UseAiInsightPollingParams): void {
  // Keep the latest `poll` without making it an effect dependency: a new
  // closure each render must not tear down and restart the interval (which
  // would reset the elapsed-time anchor and effectively defeat the ceiling).
  const pollRef = useRef(poll);
  pollRef.current = poll;

  useEffect(() => {
    if (!enabled || !pending) return;

    // Anchored when polling (re)starts — i.e. when `pending` first becomes true.
    const startedAt = Date.now();
    const id = setInterval(() => {
      if (Date.now() - startedAt >= maxWindowMs) {
        // AC4 — stop at the ceiling; do not create a new interval (deps are
        // unchanged), so this mount polls no further.
        clearInterval(id);
        return;
      }
      void pollRef.current();
    }, intervalMs);

    // AC3 — pending → non-pending (or unmount) tears the interval down.
    return () => clearInterval(id);
  }, [enabled, pending, intervalMs, maxWindowMs]);
}
